import React, { Component } from 'react';

import { startKotRealtime } from './kotRealtime';
import { Companyid } from '../../../shared/Config';
import { mapActionsToItems } from '../CaptainDashboard/kotActions';
import { groupPendingSpecialOrders } from '../CaptainDashboard/specialOrderApproval';

// TEMPORARY test page for SignalR (route: /pos/signalrtest). Talks to the
// standalone realtime server in the SignalRTest project, not to the real backend.
//
// The flow it tests, exactly as the Captain Dashboard will work:
//   1. a status change is POSTed in the same { Key: "<json rows>" } shape the
//      dashboard sends to Restaurant_Action_Update;
//   2. the server writes dbo.test_requestactions, and only after the commit
//      pushes ONE kotChanged;
//   3. every open page receives the push and re-fetches the full list.
// The page never updates its own list after a POST — the push has to drive it.
//
// Delete this folder and its route in POSApp.js when the test is done.

const DEFAULT_SERVER = 'http://localhost:5055';
// The realtime server now reads the real REQUESTACTIONS table and refuses to write to it,
// so this page only watches: it shows pushes and re-fetches, and changes are made from a
// real dashboard. Set to false only when the server is pointed back at a test table.
const WATCH_ONLY = true;
// Same fallback the order screens use when the user has no default branch.
const DEFAULT_BRANCH = localStorage.getItem('defaultbranch') || '00000000-0000-0000-0000-000000000001';

// Same ids the live status master uses (see specialOrderApproval.js).
const CONFIRMED_BY_CUSTOMER = 15;
const STATUS_OPTIONS = [
    { id: 15, label: 'Confirmed by customer' },
    { id: 10, label: 'Confirmed' },
    { id: 11, label: 'Sent to KOT' },
    { id: 14, label: 'Ready' },
    { id: 999, label: 'Served' },
];
const COLUMNS = [
    { statusId: 10, title: 'CONFIRMED' },
    { statusId: 11, title: 'SENT TO KOT' },
    { statusId: 14, title: 'READY' },
    { statusId: 999, title: 'SERVED' },
];

const signature = (rows) => rows.map((r) => r.requestactionid + ':' + r.statusId).join('|');

const CSS = `
.srt{padding:16px;font:14px system-ui,sans-serif}
.srt h2{margin:0 0 10px;font-size:18px}
.srt .row{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:10px}
.srt button{padding:6px 12px;border:1px solid #888;border-radius:4px;background:#f4f4f4;cursor:pointer}
.srt button:disabled{opacity:.5;cursor:default}
.srt input,.srt select{padding:5px 8px;border:1px solid #bbb;border-radius:4px}
.srt .badge{padding:3px 12px;border-radius:12px;color:#fff;font-weight:600;background:#888}
.srt .badge.connected{background:#2e7d32}.srt .badge.reconnecting{background:#ef6c00}.srt .badge.offline{background:#c62828}
.srt .stats{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px}
.srt .stat{border:1px solid #ddd;border-radius:6px;padding:6px 12px;min-width:92px}
.srt .stat b{display:block;font-size:18px}.srt .stat span{color:#666;font-size:12px}
.srt .grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;margin-bottom:14px}
.srt .col{border:1px solid #ddd;border-radius:6px;padding:8px;min-height:90px}
.srt .col h4{margin:0 0 8px;font-size:12px;letter-spacing:.04em;color:#555}
.srt .card{border:1px solid #e0e0e0;border-radius:4px;padding:6px 8px;margin-bottom:6px;background:#fafafa}
.srt .card small{color:#666}
.srt .card ul{margin:4px 0 6px;padding-left:16px}
.srt .log{height:170px;overflow:auto;background:#111;color:#ddd;padding:8px;border-radius:4px;font:12px ui-monospace,Consolas,monospace}
.srt .log .err{color:#ff8a80}.srt .log .ok{color:#a5d6a7}.srt .log .evt{color:#ffe082}
.srt .hint{color:#666;font-size:12px}
@media(max-width:600px){.srt{padding:10px}}
`;

class SignalRTest extends Component {
    state = {
        server: DEFAULT_SERVER,
        branch: DEFAULT_BRANCH,
        status: 'offline',
        rows: [],
        debounceMs: 150,
        logLines: [],
        stats: { events: 0, fetches: 0, skipped: 0, resyncs: 0, reconnects: 0, lastFetchMs: null, lastPushMs: null },
    };

    unmounted = false;
    handle = null;
    inFlight = false;
    dirty = false;
    debounceTimer = null;
    lastSig = null;
    pendingEventAt = null;   // server timestamp of the oldest push not yet painted
    wasConnected = false;

    componentDidMount() {
        this.connect();
        document.addEventListener('visibilitychange', this.onVisibility);
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.disconnect();
        clearTimeout(this.debounceTimer);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    // Tablets drop sockets silently when they sleep: re-fetch on wake.
    onVisibility = () => {
        if (!document.hidden) this.load('tab visible');
    };

    log = (msg, cls) => {
        if (this.unmounted) return;
        const line = { t: new Date().toLocaleTimeString(), msg, cls: cls || '' };
        this.setState((p) => ({ logLines: p.logLines.slice(-79).concat(line) }), () => {
            if (this.logEl) this.logEl.scrollTop = this.logEl.scrollHeight;
        });
    };

    bump = (patch) => {
        if (this.unmounted) return;
        this.setState((p) => ({ stats: { ...p.stats, ...patch(p.stats) } }));
    };

    connect = () => {
        if (this.handle) return;
        this.handle = startKotRealtime({
            url: this.state.server + '/hubs/pos?companyId=' + Companyid + '&branchId=' + this.state.branch,
            onLog: (m) => this.log(m, 'err'),
            onStatus: (s) => {
                if (this.unmounted) return;
                if (s === 'connected' && this.wasConnected && this.state.status === 'reconnecting') {
                    this.bump((st) => ({ reconnects: st.reconnects + 1 }));
                }
                this.wasConnected = this.wasConnected || s === 'connected';
                this.setState({ status: s });
                this.log('status: ' + s, s === 'connected' ? 'ok' : 'err');
            },
            onChange: (p) => {
                this.bump((st) => ({ events: st.events + 1 }));
                const n = p && p.requestIds ? p.requestIds.length : 0;
                this.log('PUSH kotChanged (' + n + ' order' + (n === 1 ? '' : 's') + ')', 'evt');
                const at = p && p.at ? Date.parse(p.at) : null;
                if (at && !this.pendingEventAt) this.pendingEventAt = at;
                this.scheduleLoad('push');
            },
            onResync: (why) => {
                this.bump((st) => ({ resyncs: st.resyncs + 1 }));
                this.log('RESYNC (' + why + ') -> full fetch', 'ok');
                this.load('resync: ' + why);
            },
        });
        this.setState({ status: this.state.status });   // re-render so the buttons reflect the new handle
    };

    disconnect = () => {
        if (this.handle) { this.handle.stop(); this.handle = null; }
        if (!this.unmounted) this.setState({ status: 'offline' });
    };

    // Debounce: a burst of pushes becomes one fetch.
    scheduleLoad = (source) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.load(source), this.state.debounceMs);
    };

    // One loader for everything. Never runs two fetches at once: a change that
    // lands mid-fetch re-runs it once afterwards, so the screen can never end up
    // behind the data it just heard about.
    load = async (source) => {
        if (this.unmounted) return;
        if (this.inFlight) { this.dirty = true; return; }
        this.inFlight = true;
        const t0 = performance.now();
        try {
            const res = await fetch(this.state.server + '/api/actions?companyId=' + Companyid);
            if (!res.ok) throw new Error('HTTP ' + res.status);
            const rows = await res.json();
            const ms = Math.round(performance.now() - t0);
            if (this.unmounted) return;

            const sig = signature(rows);
            if (sig === this.lastSig) {
                this.pendingEventAt = null;
                this.bump((st) => ({ fetches: st.fetches + 1, skipped: st.skipped + 1, lastFetchMs: ms }));
                this.log('fetch (' + source + ') ' + ms + ' ms, unchanged -> no re-render');
            } else {
                this.lastSig = sig;
                const eventAt = this.pendingEventAt;
                this.pendingEventAt = null;
                this.setState({ rows }, () => {
                    const pushMs = eventAt ? Date.now() - eventAt : null;
                    this.bump((st) => ({ fetches: st.fetches + 1, lastFetchMs: ms, lastPushMs: pushMs !== null ? pushMs : st.lastPushMs }));
                    this.log('fetch (' + source + ') ' + ms + ' ms, ' + rows.length + ' rows, painted' + (pushMs !== null ? ', push->painted ' + pushMs + ' ms' : ''), 'ok');
                });
            }
        } catch (e) {
            this.log('fetch failed (' + source + '): ' + e.message, 'err');
        } finally {
            this.inFlight = false;
            if (this.dirty) { this.dirty = false; this.load('coalesced'); }
        }
    };

    // Same shape the dashboard posts to Restaurant_Action_Update: the untouched
    // rows with only statusId changed. The page does NOT update itself after
    // this — the push has to drive the refresh, which is the point of the test.
    moveItems = async (items, statusId) => {
        if (!items.length) return;
        if (WATCH_ONLY) {
            this.log('watch only: change the status from a real dashboard and this page will follow', 'err');
            return;
        }
        const payload = items.map((it) => {
            const { id, requestId, kotNo, tableNo, tokenNo, createdTime, name, qty, status, selected, ...raw } = it;
            return { ...raw, statusId: Number(statusId) };
        });
        try {
            const res = await fetch(this.state.server + '/api/requestaction/update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Key: JSON.stringify(payload) }),
            });
            this.log('POST requestaction/update (' + payload.length + ' rows) -> ' + res.status, res.ok ? '' : 'err');
        } catch (e) {
            this.log('POST failed: ' + e.message, 'err');
        }
    };

    renderCard(group) {
        const current = Number(group.items[0].statusId);
        const known = STATUS_OPTIONS.some((o) => o.id === current);
        return (
            <div className="card" key={group.requestId + ':' + current}>
                <b>{group.no}</b> <small>{group.table ? 'Table ' + group.table : 'No table'}{group.customer ? ' · ' + group.customer : ''}</small>
                <ul>{group.items.map((it) => <li key={it.id}>{it.qty} × {it.name}</li>)}</ul>
                <select value={current} disabled={WATCH_ONLY} onChange={(e) => this.moveItems(group.items, e.target.value)}>
                    {!known && <option value={current}>status {current}</option>}
                    {STATUS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
            </div>
        );
    }

    render() {
        const { status, rows, stats, logLines, server, branch, debounceMs } = this.state;
        // The dashboard's own mapping, run on every render like the real page.
        // No menu master here, so names show as "Menu <id>".
        const items = mapActionsToItems(rows, []);
        const pending = groupPendingSpecialOrders(rows, [], CONFIRMED_BY_CUSTOMER);

        // One card per (order, status): that is the set of rows a move applies to.
        const groupsFor = (statusId) => {
            const by = {};
            items.filter((it) => Number(it.statusId) === statusId).forEach((it) => {
                (by[it.requestId] = by[it.requestId] || { requestId: it.requestId, no: it.ptsjrsr, table: it.tableNo, customer: it.customername, items: [] }).items.push(it);
            });
            return Object.values(by);
        };
        const pendingGroups = groupsFor(CONFIRMED_BY_CUSTOMER);
        const fmt = (v) => (v === null ? '–' : v + ' ms');

        return (
            <div className="srt">
                <style>{CSS}</style>
                <h2>SignalR test <span className={'badge ' + status}>{status}</span></h2>

                <div className="row">
                    <label>Test server <input size="26" value={server} disabled={!!this.handle}
                        onChange={(e) => this.setState({ server: e.target.value })} /></label>
                    <label>Branch <input size="36" value={branch} disabled={!!this.handle}
                        onChange={(e) => this.setState({ branch: e.target.value.trim() })} /></label>
                    <button onClick={this.connect} disabled={!!this.handle}>Connect</button>
                    <button onClick={this.disconnect} disabled={!this.handle}>Disconnect</button>
                    <button onClick={() => this.load('manual')}>Fetch now</button>
                    <label>Debounce <select value={debounceMs} onChange={(e) => this.setState({ debounceMs: Number(e.target.value) })}>
                        <option value={0}>0 ms</option><option value={150}>150 ms</option><option value={400}>400 ms</option>
                    </select></label>
                </div>

                <div className="stats">
                    <div className="stat"><b>{stats.events}</b><span>pushes received</span></div>
                    <div className="stat"><b>{stats.fetches}</b><span>fetches</span></div>
                    <div className="stat"><b>{stats.skipped}</b><span>skipped (unchanged)</span></div>
                    <div className="stat"><b>{stats.resyncs}</b><span>resyncs</span></div>
                    <div className="stat"><b>{stats.reconnects}</b><span>reconnects</span></div>
                    <div className="stat"><b>{fmt(stats.lastFetchMs)}</b><span>last fetch</span></div>
                    <div className="stat"><b>{fmt(stats.lastPushMs)}</b><span>last push → painted</span></div>
                </div>

                <div className="grid">
                    <div className="col">
                        <h4>PENDING APPROVAL ({pending.length})</h4>
                        {pendingGroups.length ? pendingGroups.map((g) => this.renderCard(g)) : <div className="hint">Nothing at status 15.</div>}
                    </div>
                    {COLUMNS.map((c) => {
                        const groups = groupsFor(c.statusId);
                        return (
                            <div className="col" key={c.statusId}>
                                <h4>{c.title} ({groups.length})</h4>
                                {groups.map((g) => this.renderCard(g))}
                            </div>
                        );
                    })}
                </div>

                <div className="log" ref={(el) => { this.logEl = el; }}>
                    {logLines.map((l, i) => <div key={i} className={l.cls}>{l.t}  {l.msg}</div>)}
                </div>
                <p className="hint">
                    Rows are the last 7 days of order and KOT actions. This page only watches: change an
                    order's status from a real dashboard (Captain, KOT, Packing or Waiter) in another tab and
                    this page should follow. There is no polling: the list is re-fetched on a push, on connect/reconnect,
                    and when the tab becomes visible again.
                    "push → painted" uses the server's clock, so it only means something when the server runs on this machine.
                </p>
            </div>
        );
    }
}

export default SignalRTest;
